// src/core/tracker.js
//
// MediaPipe Face Landmarker wrapper.
//
// Responsibilities:
//   - Lazy-load the WASM runtime and the .task model file from the official CDN.
//   - Run synchronous per-frame detection in VIDEO mode.
//   - Expose a uniform result shape regardless of underlying API tweaks.
//
// Phase 1 runs detection on the main thread. detectForVideo is fast (~10-15ms on
// modern devices) and the result is consumed within the same requestAnimationFrame
// tick, so the render loop never freezes. If a frame's detection takes too long,
// the next RAF will simply call detectForVideo again with a later timestamp;
// MediaPipe is stateless for VIDEO mode and handles this gracefully.
//
// Phase 1.5 will move this into a Web Worker with OffscreenCanvas-based frame
// transfer. The interface below (init / detect / dispose) is designed so that
// swap is a drop-in replacement.

import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

const WASM_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm';
const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task';

export class FaceTracker {
  constructor({ numFaces = 1, useGPU = true, runningMode } = {}) {
    this.numFaces = numFaces;
    this.useGPU = useGPU;
    // 'VIDEO' (default): MediaPipe tracks the face ROI frame-to-frame and applies
    // its own landmark smoothing — cheaper, but the smoothing adds lag on fast
    // head turns. 'IMAGE': every frame is detected from scratch with no internal
    // smoothing (more CPU, zero filter lag). ?trackmode=image to A/B.
    const qs = new URLSearchParams(window.location.search);
    this.runningMode = (runningMode || qs.get('trackmode') || 'video').toUpperCase() === 'IMAGE' ? 'IMAGE' : 'VIDEO';
    this.landmarker = null;
    this._lastTs = 0;
  }

  async init() {
    const fileset = await FilesetResolver.forVisionTasks(WASM_BASE);
    this.landmarker = await FaceLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: MODEL_URL,
        delegate: this.useGPU ? 'GPU' : 'CPU'
      },
      runningMode: this.runningMode,
      numFaces: this.numFaces,
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: true
    });
  }

  /**
   * Run detection for the current video frame.
   * @param {HTMLVideoElement} video
   * @param {number} nowMs   performance.now() at this RAF tick
   * @returns {object|null}  { landmarks, blendshapes, matrix } or null if no face
   */
  detect(video, nowMs) {
    // `video` may be an HTMLVideoElement OR the frame-locked canvas (no readyState).
    if (!this.landmarker || (video.readyState !== undefined && video.readyState < 2)) return null;

    // MediaPipe requires strictly monotonic timestamps. RAF can sometimes deliver
    // duplicate or out-of-order ticks (especially on multi-monitor setups), so we
    // clamp.
    let ts = Math.floor(nowMs);
    if (ts <= this._lastTs) ts = this._lastTs + 1;
    this._lastTs = ts;

    let result;
    try {
      result = this.runningMode === 'IMAGE'
        ? this.landmarker.detect(video)
        : this.landmarker.detectForVideo(video, ts);
    } catch (err) {
      // VIDEO mode can throw if the video element loses its source; surface upstream.
      console.warn('[FaceTracker] detectForVideo threw:', err);
      return null;
    }

    if (!result || !result.facialTransformationMatrixes ||
        result.facialTransformationMatrixes.length === 0) {
      return null;
    }

    return {
      landmarks:    result.faceLandmarks[0],                     // 478 × {x,y,z} normalised
      blendshapes:  result.faceBlendshapes?.[0]?.categories,     // 52 expression scores
      matrix:       result.facialTransformationMatrixes[0].data  // Float32Array length 16, row-major
    };
  }

  dispose() {
    if (this.landmarker) {
      this.landmarker.close();
      this.landmarker = null;
    }
  }
}
